document.addEventListener('DOMContentLoaded', () => {
  if (window.hmOrderStatusRendererLoaded) return;
  window.hmOrderStatusRendererLoaded = true;

  const page = document.getElementById('orderstatuspage');
  const input = document.getElementById('search-order-input');

  if (!page) return;

  const automated = page.querySelector('.order-status-automated');
  const fallback = page.querySelector('.order-status-fallback');
  const content = automated || fallback;
  if (!content) return;

  const status = document.createElement('p');
  status.className = 'search-order-status';
  status.setAttribute('aria-live', 'polite');
  input?.closest('.search-order-form')?.appendChild(status);
  const region = page.querySelector('.order-status-content');
  const loading = page.querySelector('.order-status-loading');
  const loadingMessage = page.querySelector('.order-status-loading-message');
  const spinner = page.querySelector('.order-status-spinner');
  const retry = page.querySelector('.order-status-retry');
  let automatedReady = false;

  function clearHighlights() {
    page.querySelectorAll('mark.order-search-highlight').forEach((mark) => {
      mark.replaceWith(document.createTextNode(mark.textContent || ''));
    });
    page.querySelectorAll('.order-status-order.is-match').forEach((order) => {
      order.classList.remove('is-match');
    });
    page.normalize();
  }

  function getTextNodes(root) {
    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          if (node.parentElement?.closest('script, style, mark')) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );
    const nodes = [];
    let node;

    while ((node = walker.nextNode())) {
      nodes.push(node);
    }

    return nodes;
  }

  function highlightTextNode(node, query) {
    const text = node.nodeValue || '';
    const lowerText = text.toLowerCase();
    let index = lowerText.indexOf(query);
    if (index === -1) return [];

    const fragment = document.createDocumentFragment();
    const marks = [];
    let cursor = 0;

    while (index !== -1) {
      fragment.appendChild(document.createTextNode(text.slice(cursor, index)));

      const mark = document.createElement('mark');
      mark.className = 'order-search-highlight';
      mark.textContent = text.slice(index, index + query.length);
      fragment.appendChild(mark);
      marks.push(mark);

      cursor = index + query.length;
      index = lowerText.indexOf(query, cursor);
    }

    fragment.appendChild(document.createTextNode(text.slice(cursor)));
    node.parentNode.replaceChild(fragment, node);

    return marks;
  }

  function formatGeneratedAt(value) {
    if (!value) return '';

    const generatedAt = new Date(value);
    if (Number.isNaN(generatedAt.getTime())) return '';

    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'long',
      timeStyle: 'short'
    }).format(generatedAt);
  }

  function buildOrderStatus(data) {
    if (!automated || !data?.groups?.length) return false;

    const generatedAtLabel = formatGeneratedAt(data.generatedAt);
    const intro = document.createElement('div');
    intro.className = 'order-status-intro';
    intro.innerHTML = `
      <h2>Enter your order number in the search bar above to easily find your place in line.</h2>
      <p>These are orders whose inclusions have been received. You will get a shipping notification once your order goes out.</p>
      ${generatedAtLabel ? `<p class="order-status-last-updated">Last updated: ${generatedAtLabel}</p>` : ''}
    `;

    const groups = document.createElement('div');
    groups.className = 'order-status-groups';

    data.groups.forEach((group, index) => {
      const details = document.createElement('details');
      details.className = 'order-status-group';
      details.dataset.groupKey = group.key || '';

      const summary = document.createElement('summary');
      summary.className = 'order-status-summary';
      summary.innerHTML = `
        <span class="order-status-summary-main">${group.label || ''}</span>
        <span class="order-status-summary-meta">${group.estimate || ''}</span>
        <span class="order-status-summary-count">${group.count || group.orders?.length || 0} orders</span>
      `;

      const orderList = document.createElement('div');
      orderList.className = 'order-status-order-grid';

      (group.orders || []).forEach((orderId) => {
        const order = document.createElement('span');
        order.className = 'order-status-order';
        order.dataset.orderId = String(orderId).toLowerCase();
        order.textContent = orderId;
        orderList.appendChild(order);
      });

      details.appendChild(summary);
      details.appendChild(orderList);
      groups.appendChild(details);
    });

    automated.replaceChildren(intro, groups);
    automated.hidden = false;
    fallback?.setAttribute('hidden', '');
    return true;
  }

  async function loadOrderStatusData() {
    automatedReady = false;
    if (input) input.disabled = true;
    region?.setAttribute('aria-busy', 'true');
    if (loading) loading.hidden = false;
    if (spinner) spinner.hidden = false;
    if (loadingMessage) loadingMessage.textContent = 'Loading order statuses…';
    if (retry) { retry.hidden = true; retry.disabled = true; }
    status.textContent = '';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      if (!automated?.dataset.orderStatusUrl) throw new Error('Missing order data URL');
      const response = await fetch(automated.dataset.orderStatusUrl, {
        cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) throw new Error('Order data unavailable');

      const data = await response.json();
      automatedReady = buildOrderStatus(data);
      if (!automatedReady) throw new Error('Order data unavailable');
      if (loading) loading.hidden = true;
      if (input) input.disabled = false;
      runOrderSearch();
    } catch (error) {
      automatedReady = false;
      if (spinner) spinner.hidden = true;
      if (loadingMessage) loadingMessage.textContent = "We couldn't load order statuses. Please try again.";
      if (retry) { retry.hidden = false; retry.disabled = false; }
    } finally {
      clearTimeout(timeout);
      region?.setAttribute('aria-busy', 'false');
    }
  }

  function runAutomatedSearch(query) {
    const matches = [];

    page.querySelectorAll('.order-status-order').forEach((order) => {
      if (!order.dataset.orderId?.includes(query)) return;

      order.classList.add('is-match');
      matches.push(order);
      order.closest('details')?.setAttribute('open', '');
    });

    if (!matches.length) {
      status.textContent = 'No matching order found.';
      return;
    }

    status.textContent = `${matches.length} match${matches.length === 1 ? '' : 'es'} found.`;
    matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function runFallbackSearch(query) {
    const searchContent = fallback || content;
    const matches = [];

    getTextNodes(searchContent).forEach((node) => {
      matches.push(...highlightTextNode(node, query));
    });

    if (!matches.length) {
      status.textContent = 'No matching order found.';
      return;
    }

    status.textContent = `${matches.length} match${matches.length === 1 ? '' : 'es'} found.`;
    matches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function runOrderSearch() {
    if (!input || !automatedReady) return;
    const query = input.value.trim().toLowerCase();
    clearHighlights();

    if (!query) {
      status.textContent = '';
      return;
    }

    if (automatedReady) {
      runAutomatedSearch(query);
      return;
    }

    runFallbackSearch(query);
  }

  input?.addEventListener('input', runOrderSearch);
  input?.addEventListener('search', runOrderSearch);
  retry?.addEventListener('click', loadOrderStatusData);
  loadOrderStatusData();
});
